import { useMemo } from 'react';

// V4-FRONT-ANY-USAGE 2026-05-28 — hook only checks `.length`, accept any read-only array.
// Callers pass typed WatchTopic[]/Source[]/CommunicationChannel[] arrays; we don't constrain
// to those exact types here to keep the hook loosely coupled (it doesn't read item fields).
export interface SetupWizardData {
  systemPrompt: string;
  isPositioningTemplate?: boolean;
  watchTopics: readonly unknown[];
  sources: readonly unknown[];
  communicationChannels: readonly unknown[];
  // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — optional, used by onboarding gating to lock
  // Analytics / Engagement / Publications tant qu'aucun post n'a été généré.
  posts?: readonly unknown[];
  // V4-ONBOARDING-CHECKLIST-OPTIONAL — préférence de veille sectorielle du space.
  // `false` ⇒ profil personal branding sans actualité : thématiques + sources
  // deviennent OPTIONNELLES (elles ne bloquent plus l'accès aux canaux / posts,
  // et la checklist les affiche « optionnel » plutôt que « à faire »).
  // `undefined`/`true` ⇒ comportement historique (veille requise).
  sectorWatchEnabled?: boolean;
}

export function useSetupWizard(data: SetupWizardData) {
  const {
    systemPrompt,
    isPositioningTemplate = false,
    watchTopics,
    sources,
    communicationChannels,
    posts,
    sectorWatchEnabled,
  } = data;

  // Evaluate the completion of the 4 steps
  // Positioning is complete if it has content AND it's not the default template
  const hasPositioning = !!systemPrompt && systemPrompt.trim().length > 0 && !isPositioningTemplate;
  const hasWatchTopic = watchTopics && watchTopics.length > 0;
  const hasSource = sources && sources.length > 0;
  const hasChannel = communicationChannels && communicationChannels.length > 0;
  // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — true dès qu'il existe au moins 1 post (draft
  // ou publié). Permet d'unlock Analytics / Engagement / Publications (qui n'ont
  // aucun sens à montrer avant la première génération).
  const hasPost = !!posts && posts.length > 0;

  // V4-ONBOARDING-CHECKLIST-OPTIONAL — veille désactivée ⇒ topics/sources optionnels.
  const watchOptional = sectorWatchEnabled === false;
  // « Satisfait » pour la cascade de déblocage : soit l'étape existe, soit elle est
  // optionnelle pour ce profil.
  const topicsSatisfied = hasWatchTopic || watchOptional;
  const sourcesSatisfied = hasSource || watchOptional;

  // The wizard is complete if all required steps are done (topics/sources optionnels
  // pour un profil personal branding sans veille).
  const isComplete = useMemo(() => {
    return hasPositioning && topicsSatisfied && sourcesSatisfied && hasChannel;
  }, [hasPositioning, topicsSatisfied, sourcesSatisfied, hasChannel]);

  return {
    isComplete,
    steps: {
      hasPositioning,
      hasWatchTopic,
      hasSource,
      hasChannel,
      hasPost,
      // V4-ONBOARDING-CHECKLIST-OPTIONAL — exposés pour le gating + l'affichage checklist.
      watchOptional,
      topicsSatisfied,
      sourcesSatisfied,
    }
  };
}
